import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteBreadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, topics } from "@/utils/content";
import { useSeo } from "@/hooks/useSeo";

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTopic = searchParams.get("topic") ?? "all";
  const [keyword, setKeyword] = useState(searchParams.get("keyword") ?? "");

  useSeo({
    title: "吃瓜快讯 - 圈内八卦内幕与娱乐小道消息每日更新 | 瓜田秘闻社",
    description:
      "瓜田秘闻社吃瓜快讯栏目，每日更新娱乐八卦、剧组秘闻、综艺内幕与私下交集类小道消息，支持按专题筛选和关键词搜索，快速找到你想看的那一口瓜。",
    keywords: "黑料吃瓜,吃瓜快讯,圈内八卦,娱乐内幕,小道消息",
    canonical: "/news",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "吃瓜快讯",
      description: "娱乐八卦、剧组秘闻与综艺内幕的每日快讯集合页",
      inLanguage: "zh-CN",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sdbjdz.cn/" },
          { "@type": "ListItem", position: 2, name: "吃瓜快讯", item: "https://sdbjdz.cn/news" },
        ],
      },
    },
  });

  const filtered = useMemo(() => {
    const key = keyword.trim();
    return articles.filter((article) => {
      const matchTopic = activeTopic === "all" || article.topicId === activeTopic;
      const matchKey =
        !key ||
        article.title.includes(key) ||
        article.summary.includes(key) ||
        article.tags.some((tag) => tag.includes(key));
      return matchTopic && matchKey;
    });
  }, [activeTopic, keyword]);

  const handleTopic = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("topic");
    else next.set("topic", id);
    setSearchParams(next, { replace: true });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("keyword", keyword.trim());
    else next.delete("keyword");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <SiteBreadcrumbs items={[{ label: "首页", to: "/" }, { label: "吃瓜快讯" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            吃瓜快讯
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            每天把值得看的圈内消息整理成短篇，配上时间线与背景，让你三分钟就能跟上节奏。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              name="keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜搜看：换角、剪辑、代言、热搜……"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => handleTopic("all")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              activeTopic === "all"
                ? "bg-accent-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-accent-100"
            }`}
          >
            全部
          </button>
          {topics.map((topic) => (
            <button
              key={topic.id}
              type="button"
              onClick={() => handleTopic(topic.id)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeTopic === topic.id
                  ? "bg-accent-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-accent-100"
              }`}
            >
              {topic.name}
            </button>
          ))}
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 篇内容
          {keyword.trim() && (
            <span>
              ，关键词「<span className="text-primary-700">{keyword.trim()}</span>」
            </span>
          )}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-unhappy-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-semibold text-foreground-900">
              这口瓜田里暂时没找到
            </p>
            <p className="mt-2 text-sm text-foreground-600">换个关键词，或者看看全部快讯吧。</p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
              className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i> 查看全部
            </button>
          </div>
        )}
      </div>
    </div>
  );
}